<template>
    <v-container fluid>
        <div v-if="!dataLoading">
            <v-row>
                <v-col>
                    <h4 class="text-h4">Supplier Payment #{{data.code}}</h4>
                    <p class="text-subtitle-1">Supplier Payment Modification Form</p>
                    <v-divider></v-divider>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" md="3">
                    <v-btn @click="exchangeRateIndexShown = true" depressed block>
                        <v-icon left>mdi-chart-areaspline-variant</v-icon>
                        currency exchange
                    </v-btn>
                </v-col>
            </v-row>
            <v-row>
                <v-col>
                    <PurchasePaymentForm
                            v-model="data"
                            ref="form"
                            :exchange-rate-index-shown.sync="exchangeRateIndexShown"
                            edit-mode
                        />
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" md="3">
                    <v-btn block color="primary" @click="handleSave">
                        <v-icon left>mdi-content-save</v-icon>
                        save
                    </v-btn>
                </v-col>
                <v-col cols="12" md="3">
                    <v-btn block depressed @click="handleCancel">
                        cancel
                    </v-btn>
                </v-col>
            </v-row>
        </div>
        <div v-else>
            <v-row>
                <v-col>
                    <v-skeleton-loader type="heading" class="my-4"/>
                    <v-skeleton-loader type="text" class="my-4"/>
                    <v-divider></v-divider>
                </v-col>
            </v-row>
            <v-row>
                <v-col class="d-flex">
                    <v-skeleton-loader type="button" class="my-4 me-4"/>
                    <v-skeleton-loader type="button" class="my-4"/>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" md="6">
                    <v-skeleton-loader type="text" class="my-4"/>
                    <v-skeleton-loader type="text" class="my-4"/>
                    <v-skeleton-loader type="text" class="my-4"/>
                    <v-skeleton-loader type="text" class="my-4"/>
                </v-col>
            </v-row>
            <v-row no-gutters>
                <v-col>
                    <v-skeleton-loader type="text" class="my-2"/>
                    <v-skeleton-loader type="text" class="my-2"/>
                    <v-skeleton-loader type="text" class="my-2"/>
                    <v-skeleton-loader type="text" class="my-2"/>
                    <v-skeleton-loader type="text" class="my-2"/>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" md="6">
                    <v-skeleton-loader type="text" class="my-2"/>
                    <v-skeleton-loader type="text" class="my-2"/>
                </v-col>
                <v-col cols="12" md="6">
                    <v-skeleton-loader type="text" class="my-2"/>
                    <v-skeleton-loader type="text" class="my-2"/>
                </v-col>
            </v-row>
            
        </div>
        <PAlert v-model="alert.value" v-bind="alert"/>
    </v-container>
</template>

<script>
import permissions from './../../data/permissions';
import PurchasePaymentForm from '../../components/purchase/PurchasePaymentForm.vue';
import PAlert from './../../components/common/PAlert';

import { mapActions, mapGetters } from 'vuex';

export default {
    name: "PurchasePaymentEditView",
    components: {
        PurchasePaymentForm,
        PAlert,
    },
    data() {
        return {
            id: null,
            loading: false,
            dataLoading: false,
            alert: {
                value: false,
                title: "Alert",
                content: "Are you sure?",
                callback: null
            },
            data: {},
            perm: permissions,
            exchangeRateIndexShown: false
        }
    },
    computed: {
        ...mapGetters({
            hasPermission: "authentication/hasPermission"
        })
    },
    methods: {
        ...mapActions({
            update: "purchase/payment/update",
            snackbar: "ui/snackbar/show",
            findByID: "purchase/payment/findByID",
            pushSupplier: "purchase/supplier/push",
            remove: "purchase/payment/remove"
        }),
        handleNewLine() {
            this.$refs.form.addLine()
        },
        handleSave() {
            this.alert = {...this.alert, content: "Are you sure you want to save?", value: true, callback: this.save }
        },
        save() {
            if(!this.$refs.form.validate()) return;
            this.loading = true;
            this.update(this.data).then(response => {
                this.$router.push({name: "PurchasePaymentDisplay", params: {id: response.data.id}})
                this.snackbar({text: "Payment Updated", color: "success"})
            }).catch(error => {
                let message = error.response.data.message || error.message;
                this.snackbar({text: message, color: "error"})
            }).finally(() => {
                this.loading = false;
            });
        },
        fetch() {
            this.dataLoading = true;
            this.findByID(this.id).then(response => {
                let data = response.data;
                this.pushSupplier(data.supplier);
                data = {
                    ...data,
                    supplier_id: data.supplier.id,
                    ledger_account_id: data.ledger_account.id,
                    account_id: data.account.id,
                    cheques: data.cheques.map(cheque => {
                        return {
                            ...cheque,
                            ledger_account_id: cheque.ledger_account.id
                        }
                    })
                }
                this.data = data;
            }).finally(() => {
                this.dataLoading = false;
            })
        },
        handleList() {
            this.$router.push({name: "PurchasePaymentList"})
        },
        removeAsync() {
            this.loading = true;
            this.remove(this.data).then(() => {
                this.snackbar({color: "success", text: "Payment Deleted Successfully"});
                this.$router.push({name: "PurchasePaymentList"})
            }).catch(error => {
                let message = error.response ? error.response.data.message : error;
                this.snackbar({color: "error", text: message});
            }).finally(() => {
                this.loading = false;
            })
        },
        handleRemove() {
            this.alert = {...this.alert, value: true, callback: this.removeAsync}
        },
        handleCancel() {
            this.alert = {...this.alert, content: "Are you sure you want to discard changes?", value: true, callback: this.cancel }
        },
        cancel() {
            this.$router.back();
        }
    },
    created() {
        this.id = this.$route.params.id;
        this.fetch();
    },
    beforeRouteUpdate(to, from, next) {
        this.id = to.params.id; 
        this.fetch();
        next();
    }
}
</script>

<style>

</style>